import { useEffect, useRef, useState } from "react";
import { MILESTONE_TEXT, type CollectionView, type MilestoneKey } from "../../../shared/milestones";
import { rarityLabel } from "../../../shared/rarity";
import { useI18n } from "../../i18n/I18nProvider";

/**
 * "Tu carta evolucionó": the previous card glows white and turns into the new one (a fade when the
 * visitor prefers less motion), with the milestones that got them there.
 */
export function EvolutionModal({ collection, from, onClose }: { collection: CollectionView; from: number; onClose: () => void }) {
  const { t, locale } = useI18n();
  const box = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<"before" | "after">("before");
  const before = collection.cards[Math.max(1, from) - 1];
  const after = collection.cards[collection.level - 1];
  // The newest milestones, as many as the levels gained.
  const gained = collection.milestones
    .filter((m) => m.reachedAt)
    .sort((a, b) => (b.reachedAt! > a.reachedAt! ? 1 : -1))
    .slice(0, Math.max(1, collection.level - from));

  useEffect(() => {
    box.current?.focus();
    const id = window.setTimeout(() => setPhase("after"), 1600);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => (window.clearTimeout(id), document.removeEventListener("keydown", onKey));
  }, [onClose]);

  if (!after?.image) return null;
  const rarity = rarityLabel(after.rarity, locale) ?? after.rarity;
  return (
    <div className="evo-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="evo-modal" role="dialog" aria-modal="true" aria-labelledby="evo-title" tabIndex={-1} ref={box}>
        <h2 id="evo-title">{phase === "before" ? t("cards.evolving") : t("cards.levelUp", { level: collection.level })}</h2>
        <div className={`evo-stage is-${phase}`}>
          {before?.image && <img className="evo-card evo-old" src={before.image} alt="" width={240} height={334} />}
          <img className="evo-card evo-new" src={after.image} alt={`${after.name} (${rarity})`} width={240} height={334} />
          <span className="evo-flash" aria-hidden="true" />
        </div>
        {phase === "after" && (
          <>
            <p className="evo-name">
              {after.name} · <span className="muted">{rarity}</span>
            </p>
            <ul className="evo-reasons">
              {gained.map((m) => (
                <li key={m.key}>✓ {MILESTONE_TEXT[m.key as MilestoneKey]?.[locale] ?? m.key}</li>
              ))}
            </ul>
          </>
        )}
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t("cards.great")}
        </button>
      </div>
    </div>
  );
}
